<script setup lang="ts">
/**
 * Hero unifié partagé entre /recherche/[[type]] et les pages programmatiques
 * /cagnottes/[ville], /evenements/[ville], /adhesions/[ville], /associations/ville/[ville].
 *
 * Karim 2026-06-15 (SEO sprint v4 — UX unifiée).
 *
 * Compose : H1 + intro SEO + chip ville (optionnel) + searchbar + filtres
 * catégorie + tabs cross-types. Les tabs propagent la ville si présente
 * (/cagnottes/lyon → /evenements/lyon, etc.) — cf. RechercheTabsBar.villeSlug.
 *
 * Search behavior : quand on tape dans la searchbar avec une ville pré-sélectionnée,
 * on REDIRIGE vers /recherche/X?q=... (la recherche élargit nationalement, on
 * sort du contexte ville). Décidé avec Karim car la searchbar avec ville
 * pré-filtrée serait probablement vide en pratique.
 */
import {
  RECHERCHE_THEMES,
  choixTraduits,
} from "~/composables/useRechercheHelpers";

const props = defineProps<{
  /** Type actif : tout | cagnottes | billetterie | adhesions | associations | qr-codes */
  activeType: "tout" | "cagnottes" | "evenements" | "adhesions" | "associations" | "boutiques" | "qr-codes";
  /** Titre H1 affiché (SEO) */
  h1: string;
  /** Paragraphe d'intro sous le H1 (SEO) */
  intro: string;
  /** Placeholder de la searchbar */
  placeholder?: string;
  /** Ville sélectionnée (chip affiché, propagation tabs). Slug URL-safe ex: "lyon" */
  villeSlug?: string | null;
  /** Nom humain de la ville (ex: "Lyon") affiché dans le chip */
  villeName?: string | null;
  /** Catégorie sélectionnée (pour le dropdown filtres) */
  selectedCategory?: string;
}>();

const emit = defineEmits<{
  "update:selectedCategory": [value: string];
}>();

const { t } = useI18n({ useScope: "local" });

const router = useRouter();

// Searchbar : reset à vide à chaque mount (pas de v-model parent — recherche
// déclenche un router.push vers /recherche/[type] avec param q).
const searchQuery = ref("");

const localSelectedCategory = computed({
  get: () => props.selectedCategory ?? "all",
  set: (v: string) => emit("update:selectedCategory", v),
});

// ─── Villes dynamiques (Karim 2026-06-15) ─────────────────────────────
// Fetch les villes ayant ≥1 entité du type actif, pour les afficher dans
// le filtre Ville du popover. Endpoint réutilisé sans `?city=` paramètre
// → renvoie la liste agrégée.
interface CityOption {
  slug: string;
  name: string;
  count: number;
}

const citiesEndpoint = computed(() => {
  if (props.activeType === "cagnottes") return "/api/campaigns/by-city";
  if (props.activeType === "evenements") return "/api/events/by-city";
  if (props.activeType === "adhesions") return "/api/memberships/by-city";
  if (props.activeType === "associations") return "/api/organizations/by-city";
  // Étage ville des boutiques (2026-08-29), même pivot que les cagnottes.
  if (props.activeType === "boutiques") return "/api/shops/by-city";
  return null;
});

const { data: citiesData } = await useFetch<{ cities?: CityOption[] }>(
  () => citiesEndpoint.value || "/api/campaigns/by-city",
  {
    key: `cities-${props.activeType}`,
    server: true,
    lazy: false,
    default: () => ({ cities: [] }),
  },
);

const availableCities = computed<CityOption[]>(
  () => citiesData.value?.cities || [],
);

// Navigation vers la page programmatique de la ville selon le type actif.
// Pour le type "associations" → /associations/ville/[slug] (chemin existant).
// Pour les autres → /<type>/[slug].
const goToVille = (slug: string) => {
  if (props.activeType === "associations") {
    router.push(`/associations/ville/${slug}`);
  } else if (
    props.activeType === "cagnottes" ||
    props.activeType === "evenements" ||
    props.activeType === "adhesions" ||
    props.activeType === "boutiques"
  ) {
    router.push(`/${props.activeType}/${slug}`);
  } else {
    // tab "tout" ou "qr-codes" : on tombe sur /recherche?ville=...
    router.push(`/recherche?ville=${slug}`);
  }
};

// Catégories du dropdown filtres selon type actif
// Thèmes : liste commune à tous les rayons (2026-09-26).
const activeCategories = computed(() =>
  ["associations", "cagnottes", "evenements", "adhesions", "boutiques"].includes(props.activeType)
    ? choixTraduits(RECHERCHE_THEMES, "theme")
    : [],
);

const computedPlaceholder = computed(() => {
  if (props.placeholder) return props.placeholder;
  const cityHint = props.villeName ? t('placeholder.villeHint', { ville: props.villeName }) : "";
  if (props.activeType === "cagnottes") return t('placeholder.cagnotte', { cityHint });
  if (props.activeType === "evenements") return t('placeholder.evenement', { cityHint });
  if (props.activeType === "adhesions") return t('placeholder.adhesion', { cityHint });
  if (props.activeType === "associations") return t('placeholder.association', { cityHint });
  if (props.activeType === "boutiques") return t('placeholder.boutique', { cityHint });
  return t('placeholder.generique', { cityHint });
});

// Quand l'user tape, on redirige vers /recherche/[type] pour élargir
// nationalement (cf. décision Karim point 3).
watch(searchQuery, (q) => {
  if (!q || !q.trim()) return;
  // Sur une page programmatique avec ville → redirect vers /recherche
  if (props.villeSlug) {
    const targetType = props.activeType === "tout" ? "" : `/${props.activeType}`;
    router.push(`/recherche${targetType}?q=${encodeURIComponent(q.trim())}`);
  }
});

// Retire le filtre ville → retourne sur /recherche/[type] sans ville
const removeVille = () => {
  const targetType = props.activeType === "tout" ? "" : `/${props.activeType}`;
  router.push(`/recherche${targetType}`);
};
</script>

<template>
  <div class="pt-36 sm:pt-40 lg:pt-44 pb-2 px-4 bg-[#fafcff] border-b border-gray-200">
    <div class="max-w-6xl mx-auto">
      <!-- SEO heading + intro -->
      <div class="mb-6 sm:mb-8 text-center">
        <h1 class="text-2xl sm:text-3xl lg:text-4xl font-bold text-gray-900 mb-3">
          {{ h1 }}
        </h1>
        <p class="text-sm sm:text-base text-gray-600 max-w-2xl mx-auto leading-relaxed">
          {{ intro }}
        </p>
      </div>

      <!-- Chip ville (si filtre actif) -->
      <div v-if="villeSlug && villeName" class="flex justify-center mb-4">
        <button
          type="button"
          class="inline-flex items-center gap-1.5 px-3 py-1.5 bg-main/10 hover:bg-main/15 text-main rounded-full text-sm font-medium transition-colors"
          :aria-label="t('chip.retirer', { ville: villeName })"
          @click="removeVille"
        >
          <UIcon name="i-lucide-map-pin" class="size-4" />
          <span>{{ villeName }}</span>
          <UIcon name="i-lucide-x" class="size-4" />
        </button>
      </div>

      <!-- Searchbar + filtres (catégorie + ville dynamique) -->
      <RechercheSearchFilters
        v-model="searchQuery"
        v-model:selected-category="localSelectedCategory"
        :placeholder="computedPlaceholder"
        :categories="activeCategories"
        :cities="availableCities"
        :selected-ville="villeSlug"
        @select-ville="goToVille"
        @clear-ville="removeVille"
      />

      <!-- Tabs (propagent la ville si présente) -->
      <RechercheTabsBar :active-type="activeType" :ville-slug="villeSlug" />
    </div>
  </div>
</template>

<i18n lang="json" src="../../../i18n/pages/components/recherche-page/RechercheUnifiedHero.json"></i18n>
